import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client/react';

import DeleteModal from '@atom/components/common/DeleteModal';
import UserThumbnail from '@atom/components/common/UserThumbnail';
import InheritanceLockIcon from '@atom/components/workTemplate/InheritanceLockIcon';
import WorkTemplateContext from '@atom/components/workTemplate/WorkTemplateContext';
import { WORK_TEMPLATE_TASK_ASSIGNEE_DELETE } from '@atom/graph/workTemplate';
import { Icon, IconButton } from '@atom/mui';
import { CloseIcon } from '@atom/mui/icons';
import { TaskUser } from '@atom/types/task';
import { InheritedComponentType } from '@atom/types/workTemplate';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { isComponentInherited } from '@atom/utilities/workTemplateUtilities';

import classes from './teamSection.module.css';

interface Props {
  user: TaskUser;
}

const TaskUserTile = ({ user }: Props) => {
  const { workTemplate, task, refetch } = useContext(WorkTemplateContext);

  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);

  const [taskUserRemove] = useMutation(WORK_TEMPLATE_TASK_ASSIGNEE_DELETE, {
    onCompleted: () => {
      refetch();
    },
  });

  const removeUser = async () => {
    await taskUserRemove({
      variables: {
        input: {
          taskId: task.id,
          workTemplateId: workTemplate.id,
          assigneeId: user.id,
        },
      },
    });

    setRemoveModalOpen(false);
    refetch();
  };

  const userName = `${user.firstName} ${user.lastName}`;

  const isDisabled =
    workTemplate?.published || doesNotHaveRolePermissions(ROLE_SETS.ORG_ADMIN);

  const isItemInherited = isComponentInherited(
    InheritedComponentType.TASK_ASSIGNEE,
    task?.inheritedComponents || [],
    user?.id,
  );

  const actionButton = isItemInherited ? (
    <div className={classes.lockContainer}>
      <InheritanceLockIcon
        tooltip="Inherited Team member cannot be removed or edited."
        placement="bottom-start"
      />
    </div>
  ) : isDisabled ? (
    <div />
  ) : (
    <IconButton
      onClick={() => setRemoveModalOpen(true)}
      tooltip="Remove"
      edge="end"
      disabled={isDisabled}
    >
      <Icon icon={CloseIcon} />
    </IconButton>
  );

  return (
    <>
      <div className={classes.userContainer}>
        <div className={classes.userContentContainer}>
          <UserThumbnail image={user.photoUrl} alt={userName} />
          <div className={classes.userDetails}>
            <div className={classes.name}>{userName}</div>
          </div>
        </div>
        {actionButton}
      </div>
      <DeleteModal
        open={removeModalOpen}
        onCancel={() => setRemoveModalOpen(false)}
        onConfirm={removeUser}
        title="Remove User From Task"
        confirmText="Remove"
        content="Are you sure you want to remove this user from the task?"
      />
    </>
  );
};

export default TaskUserTile;
